import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const initialFormData = {
  name: "",
  email: "",
  password: ""
};

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin-settings/admins", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setAdmins(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching admins:", error);
      alert("Failed to load admin users directory");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingAdminId) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        
        const res = await axios.put(
          API_BASE_URL + `/api/admin-settings/admins/${editingAdminId}`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        setAdmins((current) =>
          current.map((item) => (item._id === editingAdminId ? res.data : item))
        );
        alert("Admin user updated successfully");
      } else {
        const res = await axios.post(
          API_BASE_URL + "/api/admin-settings/admins",
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        setAdmins((current) => [res.data, ...current]);
        alert("Admin user created successfully");
      }
      closeForm();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Error saving admin user details");
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this admin user?")) return;
    try {
      await axios.delete(API_BASE_URL + `/api/admin-settings/admins/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setAdmins((current) => current.filter((item) => item._id !== id));
      alert("Admin user deleted successfully");
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to delete admin user");
    }
  };

  const openCreateForm = () => {
    setFormData(initialFormData);
    setEditingAdminId(null);
    setShowPassword(false);
    setShowForm(true);
  };

  const openEditForm = (admin) => {
    setFormData({
      name: admin.name || "",
      email: admin.email || "",
      password: ""
    });
    setEditingAdminId(admin._id);
    setShowPassword(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData(initialFormData);
    setEditingAdminId(null);
  };

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) =>
      [a.name, a.email].join(" ").toLowerCase().includes(q)
    );
  }, [admins, search]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b pb-4 gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Admin Directory</h1>
          <p className="text-sm text-slate-500 mt-1">Manage future administrative users and credentials.</p>
        </div>
        <button
          onClick={openCreateForm}
          className="flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 font-semibold text-sm shadow-md transition self-start sm:self-auto"
        >
          <span className="material-symbols-outlined text-sm">person_add</span>
          Add New Admin
        </button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex justify-between items-center border-b pb-3 mb-4">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">
              {editingAdminId ? "Edit Admin User" : "Register Admin User"}
            </h2>
            <button onClick={closeForm} className="text-slate-400 hover:text-slate-600">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Full Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400 text-slate-900 dark:text-white"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Email ID</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400 text-slate-900 dark:text-white"
              />
            </div>

            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                {editingAdminId ? "Password (Leave blank to keep unchanged)" : "Password"}
              </label>
              <input
                type={showPassword ? "text" : "password"}
                required={!editingAdminId}
                value={formData.password}
                onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400 text-slate-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword((c) => !c)}
                className="text-left text-xs text-blue-600 hover:underline mt-1 font-semibold"
              >
                {showPassword ? "Hide Password" : "Show Password"}
              </button>
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow transition"
              >
                {saving ? "Saving..." : editingAdminId ? "Update Admin Details" : "Register Admin"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Directory list */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800 dark:text-white">Admin Users List</h2>
          <input
            type="text"
            placeholder="Search admins..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2 text-xs outline-none focus:border-blue-400 w-48 sm:w-64 text-slate-900 dark:text-white"
          />
        </div>

        {loading ? (
          <div className="text-center py-6 text-slate-500">Loading admin list...</div>
        ) : filteredAdmins.length === 0 ? (
          <div className="text-center py-6 text-slate-500 italic">No admin users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                  <th className="py-3.5 px-4">Name</th>
                  <th className="py-3.5 px-4">Email ID</th>
                  <th className="py-3.5 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredAdmins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition">
                    <td className="py-4 px-4 font-semibold text-slate-900 dark:text-white">{admin.name}</td>
                    <td className="py-4 px-4 text-slate-600 dark:text-slate-400">{admin.email}</td>
                    <td className="py-4 px-4 text-center flex justify-center gap-2">
                      <button
                        onClick={() => openEditForm(admin)}
                        className="flex items-center gap-1 rounded-lg border border-yellow-500 hover:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 px-3 py-1.5 text-xs font-semibold transition"
                      >
                        <span className="material-symbols-outlined text-xs">edit</span>
                        Edit
                      </button>
                      <button
                        onClick={() => deleteAdmin(admin._id)}
                        disabled={admin.email === "admin@genmech.com" || admin.email === localStorage.getItem("email")}
                        className="flex items-center gap-1 rounded-lg border border-red-500 hover:bg-red-500/10 text-red-500 px-3 py-1.5 text-xs font-semibold transition disabled:opacity-40"
                      >
                        <span className="material-symbols-outlined text-xs">delete</span>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
